import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Camera, GitMerge, Grid3X3, MapPin, Spline } from "lucide-react";
import Tooltip from "../ui/Tooltip";
import { useJoints, useTendons } from "../../stores/sceneStore";
import { useSelectionStore } from "../../stores/selectionStore";
import {
  addDefaultBody,
  addDefaultCollisionGeom,
  addDefaultInertia,
  addDefaultJoint,
  addDefaultSite,
  addDefaultVisualGeom,
} from "../../utils/bodyAddActions";
import { addDefaultBodyConstraint, addDefaultTendon } from "../../utils/constraintAddActions";
import { focusNewEntityInInspector } from "../../utils/focusNewEntityInInspector";
import {
  NON_FREE_JOINT_TYPE_OPTIONS,
  getJointTypeMenuOptions,
} from "../../utils/jointTypeValidation";
import { nextAvailableCameraName } from "../../utils/nextAvailableCameraName";
import { rememberAutoParentForSelectedItem } from "../../utils/pendingAutoParentDeletion";
import { EllipsoidIcon, ExplosionIcon, JointIcon } from "../ui/Icons";
import { dispatchViewportCameraVisibility } from "../draggable-panels/scene-inventory/sceneInventoryViewportEvents";
import { BodyJointTypeInlineMenu } from "./BodyJointSections";

/** Default geom kind when adding from the inspector Quick Add icon row. */
const DEFAULT_INSPECTOR_ADD_GEOM_KIND = "box";
const BODY_ADD_MENU_BUTTON_CLASS =
  "menu-item ring-0 focus-visible:ring-0 focus-visible:outline-none shrink-0 h-8 w-8 rounded-md border flex items-center justify-center panel-text-helper transition-colors hover:bg-gray-700/60 hover:text-gray-200";
const BODY_ADD_MENU_DISABLED_BUTTON_CLASS =
  "menu-item ring-0 focus-visible:ring-0 focus-visible:outline-none shrink-0 h-8 w-8 rounded-md border flex items-center justify-center panel-text-helper cursor-not-allowed opacity-35";
const BODY_ADD_MENU_BORDER_STYLE = { backgroundColor: "transparent", borderColor: "rgb(55 65 81)" };

const EMPTY_INSPECTOR_COLLISION_GEOM_KIND = "sphere";

const BodyAddInlineMenu = React.memo(function BodyAddInlineMenu({
  allowRootBody = false,
  bodyUuid,
  bodyName,
  bodies,
  cameras,
  equalities,
  hasInertia,
  disabled,
  sceneActions,
}) {
  const joints = useJoints();
  const tendons = useTendons();
  const [jointTypeRowOpen, setJointTypeRowOpen] = useState(false);
  const runAddInline = useCallback(
    (createFn) => {
      if (disabled || (!bodyUuid && !allowRootBody)) return;
      createFn();
    },
    [allowRootBody, bodyUuid, disabled]
  );
  const addJointOptions = useMemo(() => {
    if (!bodyUuid && allowRootBody) return NON_FREE_JOINT_TYPE_OPTIONS;
    return getJointTypeMenuOptions({
      bodies,
      joints,
      bodyUuid,
      includeFree: false,
    });
  }, [allowRootBody, bodies, bodyUuid, joints]);
  const jointDisabledReason = useMemo(() => {
    if (!bodyUuid && !allowRootBody) return "Choose a body before adding a joint.";
    const hasEnabledOption = addJointOptions.some((option) => !option.disabled);
    if (hasEnabledOption) return "";
    return (
      addJointOptions.find((option) => option.disabled && option.description)?.description ||
      "No joint types are available for this body."
    );
  }, [addJointOptions, allowRootBody, bodyUuid]);
  const jointDisabled = !!jointDisabledReason;

  useEffect(() => {
    if (jointDisabled && jointTypeRowOpen) {
      setJointTypeRowOpen(false);
    }
  }, [jointDisabled, jointTypeRowOpen]);

  const handleAddJointByKind = useCallback(
    (kind) => {
      if (disabled || !kind || (!bodyUuid && !allowRootBody)) return;
      const parent =
        bodyUuid ||
        addDefaultBody(sceneActions, null, [0, 0, 0], {
          addFreeJoint: kind === "free",
        });
      if (!parent) return;
      const createdAutoParent = !bodyUuid;
      const addedJointUuid = addDefaultJoint(sceneActions, parent, kind);
      if (addedJointUuid) {
        setJointTypeRowOpen(false);
        if (createdAutoParent) {
          rememberAutoParentForSelectedItem({
            itemType: "joint",
            itemUuid: addedJointUuid,
            parentBodyUuid: parent,
          });
        }
        focusNewEntityInInspector(
          "sceneTree",
          () => useSelectionStore.getState().selectJoint(addedJointUuid),
          { openPropertiesPanel: false }
        );
      }
    },
    [allowRootBody, bodyUuid, disabled, sceneActions]
  );
  const preferredTendonJointUuid = useMemo(() => {
    const bodyJointUuids = bodies?.[bodyUuid]?.joints;
    if (!Array.isArray(bodyJointUuids)) return null;
    return (
      bodyJointUuids.find((jointUuid) => {
        const joint = joints?.[jointUuid];
        return joint?.kind === "hinge" || joint?.kind === "slide";
      }) || null
    );
  }, [bodies, bodyUuid, joints]);

  const renderIconButton = ({
    key,
    label,
    tooltip = label,
    Icon,
    onClick,
    disabled: itemDisabled = false,
  }) => (
    <Tooltip key={key} content={tooltip}>
      <button
        type="button"
        disabled={disabled || itemDisabled}
        aria-label={label}
        onClick={(event) => {
          event.stopPropagation();
          if (disabled || itemDisabled) return;
          onClick();
        }}
        className={
          disabled || itemDisabled
            ? BODY_ADD_MENU_DISABLED_BUTTON_CLASS
            : BODY_ADD_MENU_BUTTON_CLASS
        }
        style={BODY_ADD_MENU_BORDER_STYLE}
      >
        <Icon className="w-4 h-4" aria-hidden="true" />
      </button>
    </Tooltip>
  );

  const menuItems = [
    {
      key: "child-body",
      label: bodyUuid ? "Child body" : "Body",
      Icon: Box,
      disabled: !bodyUuid && !allowRootBody,
      onClick: () => {
        if (disabled || (!bodyUuid && !allowRootBody)) return;
        const addedBodyUuid = addDefaultBody(sceneActions, bodyUuid || null);
        if (!bodyUuid && addedBodyUuid) {
          focusNewEntityInInspector(
            "sceneTree",
            () => useSelectionStore.getState().selectBody(addedBodyUuid),
            { openPropertiesPanel: false }
          );
        }
      },
    },
    {
      key: "joint",
      label: "Joint",
      tooltip: jointDisabledReason || "Joint",
      Icon: JointIcon,
      disabled: jointDisabled,
      onClick: () => {
        if (disabled || jointDisabled) return;
        setJointTypeRowOpen((open) => !open);
      },
    },
    {
      key: "site",
      label: "Site",
      Icon: MapPin,
      onClick: () =>
        runAddInline(() => {
          const addedSiteUuid = addDefaultSite(sceneActions, bodyUuid);
          if (addedSiteUuid) {
            focusNewEntityInInspector(
              "sceneTree",
              () => useSelectionStore.getState().selectSite(addedSiteUuid),
              { openPropertiesPanel: false }
            );
          }
        }),
    },
    {
      key: "collision",
      label: "Collision",
      Icon: ExplosionIcon,
      disabled: !bodyUuid && !allowRootBody,
      onClick: () =>
        runAddInline(() => {
          const parent = bodyUuid || addDefaultBody(sceneActions, null);
          if (!parent) return;
          const createdAutoParent = !bodyUuid;
          const addedGeomUuid = addDefaultCollisionGeom(
            sceneActions,
            parent,
            createdAutoParent
              ? EMPTY_INSPECTOR_COLLISION_GEOM_KIND
              : DEFAULT_INSPECTOR_ADD_GEOM_KIND
          );
          if (addedGeomUuid) {
            if (createdAutoParent) {
              rememberAutoParentForSelectedItem({
                itemType: "geom",
                itemUuid: addedGeomUuid,
                parentBodyUuid: parent,
              });
            }
            focusNewEntityInInspector(
              "sceneTree",
              () => useSelectionStore.getState().selectGeom(`${parent}:${addedGeomUuid}`),
              { openPropertiesPanel: false }
            );
          }
        }),
    },
    {
      key: "visual",
      label: "Visual",
      Icon: Grid3X3,
      disabled: !bodyUuid && !allowRootBody,
      onClick: () =>
        runAddInline(() => {
          const parent = bodyUuid || addDefaultBody(sceneActions, null);
          if (!parent) return;
          const createdAutoParent = !bodyUuid;
          const addedGeomUuid = addDefaultVisualGeom(
            sceneActions,
            parent,
            DEFAULT_INSPECTOR_ADD_GEOM_KIND
          );
          if (addedGeomUuid) {
            if (createdAutoParent) {
              rememberAutoParentForSelectedItem({
                itemType: "geom",
                itemUuid: addedGeomUuid,
                parentBodyUuid: parent,
              });
            }
            focusNewEntityInInspector(
              "sceneTree",
              () => useSelectionStore.getState().selectGeom(`${parent}:${addedGeomUuid}`),
              { openPropertiesPanel: false }
            );
          }
        }),
    },
    {
      key: "constraint",
      label: "Constraint",
      Icon: GitMerge,
      disabled: !bodyUuid,
      onClick: () =>
        runAddInline(() => {
          const addedEqualityUuid = addDefaultBodyConstraint(
            sceneActions,
            bodies || {},
            bodyUuid,
            "connect",
            equalities || {}
          );
          if (addedEqualityUuid) {
            focusNewEntityInInspector(
              "sceneTree",
              () => useSelectionStore.getState().selectEquality(addedEqualityUuid),
              { openPropertiesPanel: false }
            );
          }
        }),
    },
    {
      key: "tendon",
      label: preferredTendonJointUuid
        ? "Tendon"
        : "Add a hinge or slide joint before adding a tendon",
      Icon: Spline,
      disabled: !bodyUuid || !preferredTendonJointUuid,
      onClick: () =>
        runAddInline(() => {
          const addedTendonUuid = addDefaultTendon(
            sceneActions,
            joints || {},
            preferredTendonJointUuid,
            tendons || {}
          );
          if (addedTendonUuid) {
            focusNewEntityInInspector(
              "sceneTree",
              () => useSelectionStore.getState().selectTendon(addedTendonUuid),
              { openPropertiesPanel: false }
            );
          }
        }),
    },
    {
      key: "camera",
      label: bodyUuid ? "Camera" : "Fixed world camera",
      Icon: Camera,
      onClick: () =>
        runAddInline(() => {
          const addedCameraUuid = bodyUuid
            ? sceneActions.addCamera({
                name: nextAvailableCameraName(cameras, {
                  kind: "mounted_body",
                  bodyName: bodyName || String(bodyUuid),
                }),
                kind: "mounted",
                position: [0, 0, 0.5],
                orientation: [1, 0, 0, 0],
                fovy: 65,
                width: 800,
                height: 800,
                fps: 8,
                mountType: "body",
                mountBodyUuid: bodyUuid,
              })
            : sceneActions.addCamera({
                name: nextAvailableCameraName(cameras, { kind: "world" }),
                kind: "fixed",
                position: [0, 0.5, 2],
                orientation: [1, 0, 0, 0],
                fovy: 65,
                width: 800,
                height: 800,
                fps: 8,
              });
          if (addedCameraUuid) {
            dispatchViewportCameraVisibility(true);
            focusNewEntityInInspector(
              "sceneTree",
              () => useSelectionStore.getState().selectCamera(addedCameraUuid),
              { openPropertiesPanel: false }
            );
          }
        }),
    },
    {
      key: "inertial",
      label: hasInertia ? "Body already has inertial" : "Inertial",
      Icon: EllipsoidIcon,
      disabled: !bodyUuid || !!hasInertia,
      onClick: () =>
        runAddInline(() => {
          const addedInertialUuid = addDefaultInertia(sceneActions, bodyUuid);
          if (addedInertialUuid) {
            focusNewEntityInInspector(
              "sceneTree",
              () => useSelectionStore.getState().selectInertial(`${bodyUuid}:${addedInertialUuid}`),
              { openPropertiesPanel: false }
            );
          }
        }),
    },
  ];

  return (
    <div className="space-y-2">
      <div className="flex w-full flex-wrap items-center gap-1 rounded-lg border border-gray-600 p-1.5">
        {menuItems.map((item) => renderIconButton(item))}
      </div>
      {jointTypeRowOpen ? (
        <BodyJointTypeInlineMenu
          className="inspector-quick-add-joint-submenu-attention"
          disabled={disabled || (!bodyUuid && !allowRootBody)}
          options={addJointOptions}
          onAddJointKind={handleAddJointByKind}
        />
      ) : null}
    </div>
  );
});

export { DEFAULT_INSPECTOR_ADD_GEOM_KIND };
export default BodyAddInlineMenu;
